import { definePreset } from '@primeuix/themes';
import Aura from '@primeuix/themes/aura';

import { MenuDark } from '../app/shared/constants/theme.constants';
import { borderRadius } from './theme.border';
import { boxShadow } from './theme.box-shadow';
import { colors } from './theme.colors';
import { screens } from './theme.responsive';
import { typography } from './theme.typography';

const primitive = {
  ...colors,
  borderRadius: borderRadius,
  boxShadow: boxShadow,
  fontSize: typography.fontSize,
  screens: screens,
};
const darkTint = (color: string, transparency: number): string =>
  `color-mix(in srgb, ${color}, transparent ${transparency}%)`;

interface DarkButtonTokens {
  outlined: { borderColor: string; color: string; hoverBackground: string; activeBackground: string };
  text: { color: string; hoverBackground: string; activeBackground: string };
}

const darkButtonSeverity = (tintColor: string, textColor: string, borderColor: string): DarkButtonTokens => ({
  outlined: {
    borderColor,
    color: textColor,
    hoverBackground: darkTint(tintColor, 88),
    activeBackground: darkTint(tintColor, 76),
  },
  text: {
    color: textColor,
    hoverBackground: darkTint(tintColor, 88),
    activeBackground: darkTint(tintColor, 76),
  },
});

/**
 * This is the master theme config for the current theme. It consists of a base theme preset by PrimeNG (in this case, Aura) plus additional theming overrides.
 * #
 * Complete docs https://primeng.org/theming. To view specific component config, follow the same schema as the preset theme (@primeng/themes/aura).
 * #
 * All primitive tokens (border radius, colors, spacing) are extracted to consistently power both TailwindCSS & PrimeNG
 */

export const PortalTheme = definePreset(Aura, {
  primitive: primitive,
  semantic: {
    primary: {
      50: '{blue.50}',
      100: '{blue.100}',
      200: '{blue.200}',
      300: '{blue.300}',
      400: '{blue.400}',
      500: '{blue.500}',
      600: '{blue.600}',
      700: '{blue.700}',
      800: '{blue.800}',
      900: '{blue.900}',
      950: '{blue.950}'
    },
    colorScheme: {
      light: {
        surface: {
          0: '#ffffff',
          50: '{slate.50}',
          100: '{slate.100}',
          200: '{slate.200}',
          300: '{slate.300}',
          400: '{slate.400}',
          500: '{slate.500}',
          600: '{slate.600}',
          700: '{slate.700}',
          800: '{slate.800}',
          900: '{slate.900}',
          950: '{slate.950}'
        },
        primary: {
          color: '{primary.500}',
          contrastColor: '#ffffff',
          hoverColor: '{primary.600}',
          activeColor: '{primary.700}'
        },
        highlight: {
          background: '{primary.50}',
          focusBackground: '{primary.100}',
          color: '{primary.700}',
          focusColor: '{primary.800}'
        },
        mask: {
          background: 'rgba(0,0,0,0.4)',
          color: '{surface.200}'
        },
        formField: {
          background: '{surface.0}',
          disabledBackground: '{surface.200}',
          filledBackground: '{surface.50}',
          filledHoverBackground: '{surface.50}',
          filledFocusBackground: '{surface.50}',
          borderColor: '{surface.300}',
          hoverBorderColor: '{surface.400}',
          focusBorderColor: '{primary.color}',
          invalidBorderColor: '{error.300}',
          color: '{surface.800}',
          disabledColor: '{surface.500}',
          placeholderColor: '{surface.400}',
          invalidPlaceholderColor: '{error.400}',
          floatLabelColor: '{surface.500}',
          floatLabelFocusColor: '{primary.500}',
          floatLabelActiveColor: '{surface.500}',
          floatLabelInvalidColor: '{form.field.invalid.placeholder.color}',
          icon: {
            color: '{surface.400}',
          },
          shadow: 'none'
        },
        text: {
          color: '{surface.800}',
          hoverColor: '{surface.900}',
          mutedColor: '{surface.500}',
          hoverMutedColor: '{surface.600}'
        },
        content: {
          background: '{surface.0}',
          hoverBackground: '{surface.100}',
          borderColor: '{surface.300}',
          color: '{text.color}',
          hoverColor: '{text.hover.color}'
        },
        overlay: {
          select: {
            background: '{surface.0}',
            borderColor: '{surface.300}',
            color: '{text.color}'
          },
          popover: {
            background: '{surface.0}',
            borderColor: '{surface.300}',
            color: '{text.color}'
          },
          modal: {
            background: '{surface.0}',
            borderColor: '{surface.300}',
            color: '{text.color}'
          }
        },
        list: {
          option: {
            focusBackground: '{surface.100}',
            selectedBackground: '{highlight.background}',
            selectedFocusBackground: '{highlight.focus.background}',
            color: '{text.color}',
            focusColor: '{text.hover.color}',
            selectedColor: '{highlight.color}',
            selectedFocusColor: '{highlight.focus.color}',
            icon: {
              color: '{surface.400}',
              focusColor: '{surface.500}'
            }
          },
          optionGroup: {
            background: 'transparent',
            color: '{text.muted.color}'
          }
        },
        navigation: {
          item: {
            focusBackground: '{surface.100}',
            activeBackground: '{surface.100}',
            color: '{text.color}',
            focusColor: '{text.hover.color}',
            activeColor: '{text.hover.color}',
            icon: {
              color: '{surface.400}',
              focusColor: '{surface.500}',
              activeColor: '{surface.500}'
            }
          },
          submenuLabel: {
            background: 'transparent',
            color: '{text.muted.color}'
          },
          submenuIcon: {
            color: '{surface.400}',
            focusColor: '{surface.500}',
            activeColor: '{surface.500}'
          }
        }
      },
      dark: {
        surface: {
          0: '#ffffff',
          50: '{slate.50}',
          100: '{slate.100}',
          200: '{slate.200}',
          300: '{slate.300}',
          400: '{slate.400}',
          500: '{slate.500}',
          600: '{slate.600}',
          700: '{slate.700}',
          800: '{slate.800}',
          900: '{slate.900}',
          950: '{slate.950}'
        },
        primary: {
          color: '{primary.300}',
          hoverColor: '{primary.400}',
          activeColor: '{primary.400}'
        },
      }
    },
    transitionDuration: '0.15s',
    focusRing: {
      width: '2px',
      style: 'solid',
      color: '{primary.300}',
      offset: '2px',
      shadow: 'none'
    },
    disabledOpacity: '0.5',
    iconSize: '{fontSize.base}',
    anchorGutter: '2px',
    formField: {
      paddingX: '1rem',
      paddingY: '0.55rem',
      sm: {
        fontSize: '{fontSize.sm}',
        paddingX: '1rem',
        paddingY: '0.4rem'
      },
      lg: {
        fontSize: '{fontSize.lg}',
        paddingX: '2rem',
        paddingY: '1.5rem'
      },
      borderRadius: '{border.radius.sm}',
      focusRing: {
        width: '0',
        style: 'none',
        color: 'transparent',
        offset: '0',
        shadow: 'none'
      },
      transitionDuration: '{transition.duration}'
    },
    list: {
      padding: '0rem',
      gap: '2px',
      header: {
        padding: '0rem'
      },
      option: {
        padding: '0.6rem 1rem',
        borderRadius: '{border.radius.sm}'
      },
      optionGroup: {
        padding: '0.8rem 1rem',
        fontWeight: '600'
      }
    },
    content: {
      borderRadius: '{border.radius.sm}'
    },
    mask: {
      transitionDuration: '0.15s'
    },
    navigation: {
      list: {
        padding: '1rem',
        gap: '0.5rem'
      },
      item: {
        padding: '0.6rem 1rem',
        borderRadius: '{border.radius.sm}',
        gap: '0.5rem'
      },
      submenuLabel: {
        padding: '0.5rem 1rem',
        fontWeight: '600'
      },
      submenuIcon: {
        size: '{fontSize.lg}'
      }
    },
    overlay: {
      select: {
        borderRadius: '{border.radius.md}',
        shadow: '{boxShadow.md}'
      },
      popover: {
        borderRadius: '{border.radius.md}',
        padding: '1rem',
        shadow: '{boxShadow.md}'
      },
      modal: {
        borderRadius: '{border.radius.lg}',
        padding: '1.25rem',
        shadow: '{boxShadow.lg}'
      },
      navigation: {
        shadow: '{boxShadow.md}'
      }
    },
  },
  components: {
    accordion: {
      header: {
        padding: '2rem'
      }
    },
    autocomplete: {
      root: {
        paddingX: '1rem',
        paddingY: '1rem',
      },
      list: {
        padding: '1rem',
      },
      // Per-scheme (like the dark menu/select) so a nested .portal-dark-theme re-declares them; the semantic
      // overlay/list tokens resolve once at :root and would stay light. Values follow the fOS dark surfaces.
      colorScheme: {
        dark: {
          overlay: {
            background: '{surface.900}',
            borderColor: '{surface.700}',
            color: '{surface.0}',
          },
          option: {
            color: '{surface.0}',
            focusBackground: '{surface.800}',
            focusColor: '{surface.0}',
            selectedBackground: '{surface.800}',
            selectedColor: '{surface.0}',
            selectedFocusBackground: '{surface.800}',
            selectedFocusColor: '{surface.0}',
          },
        },
      },
    },
    chip: {
      root: {
        gap: '0.5rem',
      },
      removeIcon: {
        size: '1.6rem',
      },
      colorScheme: {
        light: {
          root: {
            background: '{surface.200}',
          },
          removeIcon: {
            color: '{surface.500}',
          },
        },
      },
    },
    badge: {
      root: {
        fontSize: '{fontSize.sm}',
        fontWeight: '700',
        height: '2.2rem',
        minWidth: '2.2rem',
        padding: '0 0.6rem',
        borderRadius: '{borderRadius.sm}',
      },
      lg: {
        fontSize: '{fontSize.base}',
        height: '3.2rem',
        minWidth: '3.2rem',
      },
      xl: {
        fontSize: '{fontSize.lg}',
        height: '4.2rem',
        minWidth: '4.2rem',
      },
    },
    button: {
      // v21 moves all root-level button tokens (borderRadius, label, sm, lg,
      // etc.) under `root`. colorScheme stays at the top level.
      root: {
        borderRadius: '{borderRadius.sm}',
        // [rounded] = a full pill. Aura's 2rem is only 20px at our 62.5% root, short of half a large button's height.
        roundedBorderRadius: '{borderRadius.full}',
        // Badge size when a badge is rendered inside a p-button (via [badge]
        // input). v21 default is 1rem, which at our 62.5% root = 10px — too
        // small to fit the digit. 1.8rem (18px) is enough for one or two
        // digits to read clearly without crowding the button label.
        badgeSize: '1.8rem',
        label: {
          fontWeight: '600',
        },
        iconOnlyWidth: '3.4rem',
        sm: {
          paddingX: '0.5rem',
          paddingY: '0rem',
          iconOnlyWidth: '2.8rem',
        },
        lg: {
          iconOnlyWidth: '4.2rem',
          fontSize: '{fontSize.base}'
        }
      },
      colorScheme: {
        light: {
          outlined: {
            primary: {
              borderColor: '{surface.300}',
            },
            secondary: {
              borderColor: '{surface.300}',
            },
            success: {
              borderColor: '{surface.300}',
            },
            info: {
              borderColor: '{surface.300}',
            },
            warn: {
              borderColor: '{surface.300}',
            },
            help: {
              borderColor: '{surface.300}',
            },
            danger: {
              borderColor: '{surface.300}',
            },
          }
        },
        dark: {
          outlined: {
            primary: darkButtonSeverity('{primary.color}', '{primary.color}', '{primary.500}').outlined,
            secondary: {
              borderColor: '{surface.500}',
              color: '{surface.200}',
              hoverBackground: 'rgba(255,255,255,0.1)',
              activeBackground: 'rgba(255,255,255,0.2)',
            },
            success: darkButtonSeverity('{success.400}', '{success.300}', '{success.500}').outlined,
            info: darkButtonSeverity('{sky.400}', '{sky.300}', '{sky.500}').outlined,
            warn: darkButtonSeverity('{warn.400}', '{warn.300}', '{warn.500}').outlined,
            help: darkButtonSeverity('{purple.400}', '{purple.300}', '{purple.500}').outlined,
            danger: darkButtonSeverity('{error.400}', '{error.300}', '{error.500}').outlined,
          },
          text: {
            primary: darkButtonSeverity('{primary.color}', '{primary.color}', '{primary.500}').text,
            secondary: {
              color: '{surface.200}',
            },
            success: darkButtonSeverity('{success.400}', '{success.300}', '{success.500}').text,
            info: darkButtonSeverity('{sky.400}', '{sky.300}', '{sky.500}').text,
            warn: darkButtonSeverity('{warn.400}', '{warn.300}', '{warn.400}').text,
            help: darkButtonSeverity('{purple.400}', '{purple.300}', '{purple.400}').text,
            danger: darkButtonSeverity('{error.400}', '{error.300}', '{error.400}').text,
          }
        }
      }
    },
    card: {
      root: {
        borderRadius: '{borderRadius.md}',
        shadow: 'none',
      },
      title: {
        fontSize: '{fontSize.xl}',
      },
      body: {
        padding: '2rem',
        gap: '1rem'
      },
    },
    checkbox: {
      // v21 nests width/height/borderColor/sm under `root` (same pattern
      // as button, radiobutton, toggleswitch).
      root: {
        borderRadius: '{borderRadius.xs}',
        width: '2rem',
        height: '2rem',
        borderColor: '{surface.400}',
        sm: {
          width: '1.6rem',
          height: '1.6rem',
        },
      },
      icon: {
        size: '{fontSize.base}',
      },
    },
    datatable: {
      // v21 moves header/headerCell to the root datatable namespace; the
      // colorScheme.light/dark.header.cell shape used in v19 is gone.
      colorScheme: {
        light: {
          header: {
            background: '{surface.200}',
            color: '{surface.500}',
          },
          headerCell: {
            background: '{surface.200}',
            color: '{surface.500}',
            hoverBackground: '{surface.200}',
          },
        },
        // Declared per scheme (not via {content.*}) so a nested .portal-dark-theme re-declares them on that element.
        dark: {
          header: {
            background: '{surface.950}',
            borderColor: '{surface.700}',
            color: '{surface.400}',
          },
          headerCell: {
            background: '{surface.950}',
            hoverBackground: '{surface.900}',
            borderColor: '{surface.700}',
            color: '{surface.400}',
            hoverColor: '{surface.200}',
          },
          row: {
            background: '{surface.900}',
            hoverBackground: '{surface.800}',
            color: '{surface.200}',
            hoverColor: '{surface.0}',
          },
          bodyCell: {
            borderColor: '{surface.700}',
          },
          footerCell: {
            background: '{surface.950}',
            borderColor: '{surface.700}',
            color: '{surface.200}',
          },
        },
      },
    },
    datepicker: {
      panel: {
        padding: '1rem',
        borderRadius: '{borderRadius.md}',
      },
      // v21 dropped datepicker.week — the week-row day padding now inherits
      // from datepicker.date.padding below.
      date: {
        padding: '.3rem',
        width: '2.4rem',
        height: '2.4rem',
      }
    },
    dialog: {
      header: {
        padding: '2rem'
      },
      content: {
        padding: '2rem'
      },
      footer: {
        padding: '2rem',
        gap: '1rem'
      },
      title: {
        fontSize: '{fontSize.lg}',
      }
    },
    fileupload: {
      basic: {
        gap: '1rem'
      }
    },
    iconfield: {
      // Per-scheme for the same reason as inputtext: the default icon colour is tuned for a light field.
      colorScheme: {
        dark: {
          icon: {
            color: '{surface.400}',
          },
        },
      },
    },
    inputotp: {
      input: {
        width: '3.4rem',
        sm: {
          width: '3rem',
        },
        lg: {
          width: '5.4rem',
        }
      },
    },
    inputtext: {
      root: {
        borderRadius: '{borderRadius.sm}',
      },
      // Per-scheme for the same reason as autocomplete: a nested .portal-dark-theme input would otherwise stay light.
      colorScheme: {
        dark: {
          root: {
            background: '{surface.950}',
            filledBackground: '{surface.950}',
            filledHoverBackground: '{surface.950}',
            filledFocusBackground: '{surface.950}',
            borderColor: '{surface.700}',
            hoverBorderColor: '{surface.600}',
            color: '{surface.0}',
            placeholderColor: '{surface.400}',
          },
        },
      },
    },
    menu: {
      root: {
        borderRadius: '{borderRadius.md}'
      },
      item: {
        padding: '1rem',
        gap: '.8rem',
      },
      colorScheme: {
        dark: MenuDark,
      }
    },
    menubar: {
      colorScheme: {
        // v21 moved menubar.mobile out of colorScheme. Mobile-specific
        // customizations now live under the top-level `mobile` key below.
        dark: MenuDark,
      },
      root: {
        padding: '0.5rem 1rem',
        borderRadius: '{borderRadius.md}'
      },
      baseItem: {
        padding: '1rem 1.5rem',
      },
      item: {
        padding: '1rem 1.5rem',
        gap: '.8rem',
      },
      submenu: {
        borderRadius: '{borderRadius.md}'
      },
      // v21 dropped menubar.mobile.buttonSize.
    },
    message: {
      text: {
        fontSize: '{fontSize.base}',
        sm: {
          fontSize: '{fontSize.sm}'
        },
        lg: {
          fontSize: '{fontSize.base}'
        },
      },
      colorScheme: {
        light: {
          warn: {
            background: '{warn.50}',
            color: '{warn.600}',
            borderColor: '{warn.200}'
          }
        },
        dark: {
          info: {
            background: 'color-mix(in srgb, {sky.500}, transparent 82%)',
            borderColor: 'color-mix(in srgb, {sky.400}, transparent 50%)',
            color: '{sky.300}'
          },
          success: {
            background: 'color-mix(in srgb, {success.500}, transparent 82%)',
            borderColor: 'color-mix(in srgb, {success.400}, transparent 50%)',
            color: '{success.300}'
          },
          warn: {
            background: 'color-mix(in srgb, {warn.500}, transparent 82%)',
            borderColor: 'color-mix(in srgb, {warn.400}, transparent 50%)',
            color: '{warn.300}'
          },
          error: {
            background: 'color-mix(in srgb, {error.500}, transparent 78%)',
            borderColor: 'color-mix(in srgb, {error.400}, transparent 45%)',
            color: '{error.300}'
          },
          secondary: {
            background: '{surface.800}',
            borderColor: '{surface.600}',
            color: '{surface.200}'
          }
        }
      }
    },
    multiselect: {
      list: {
        header: {
          padding: '1rem'
        }
      }
    },
    paginator: {
      // v21 restructured paginator tokens — the v19 nav.button.width/height
      // and colorScheme.light.nav.button.selected.color overrides are
      // no-ops here and have been dropped.
    },
    radiobutton: {
      // v21 nests width/height/borderColor under `root` (same pattern as
      // button, checkbox, toggleswitch).
      root: {
        width: '2.2rem',
        height: '2.2rem',
        borderColor: '{surface.400}',
      },
      icon: {
        size: '{fontSize.sm}',
      },
      // Per-scheme for the same reason as inputtext: a nested .portal-dark-theme radio would otherwise stay light.
      colorScheme: {
        dark: {
          root: {
            background: '{surface.950}',
            borderColor: '{surface.600}',
            hoverBorderColor: '{surface.500}',
            checkedBackground: '{primary.color}',
            checkedBorderColor: '{primary.color}',
            checkedHoverBackground: '{primary.hoverColor}',
            checkedHoverBorderColor: '{primary.hoverColor}',
          },
          icon: {
            checkedColor: '{surface.950}',
            checkedHoverColor: '{surface.950}',
          },
        },
      },
    },
    select: {
      list: {
        padding: '0.5rem',
        gap: '0.5rem',
        header: {
          // Gap between the filter input and the option list in the overlay (--p-select-list-header-padding).
          padding: '1rem'
        }
      },
      // Per-scheme for the same reason as autocomplete: the trigger's formField tokens and the overlay's
      // semantic tokens both resolve at :root, so a nested .portal-dark-theme select would stay light.
      colorScheme: {
        dark: {
          root: {
            background: '{surface.950}',
            filledBackground: '{surface.950}',
            filledHoverBackground: '{surface.950}',
            filledFocusBackground: '{surface.950}',
            borderColor: '{surface.700}',
            hoverBorderColor: '{surface.600}',
            color: '{surface.0}',
            placeholderColor: '{surface.400}',
          },
          overlay: {
            background: '{surface.900}',
            borderColor: '{surface.700}',
            color: '{surface.0}',
          },
          option: {
            color: '{surface.0}',
            focusBackground: '{surface.800}',
            focusColor: '{surface.0}',
            selectedBackground: '{surface.800}',
            selectedColor: '{surface.0}',
            selectedFocusBackground: '{surface.800}',
            selectedFocusColor: '{surface.0}',
          },
        },
      },
    },
    skeleton: {
      // Per-scheme for the same reason as inputtext: the default fill is a light grey that vanishes on surface-900.
      colorScheme: {
        dark: {
          root: {
            background: '{surface.800}',
          },
        },
      },
    },
    slider: {
      handle: {
        height: '2.2rem',
        width: '2.2rem',
      },
      colorScheme: {
        light: {
          range: {
            background: '{primary.400}',
          },
          handle: {
            background: '{primary.500}',
          }
        },
        // Per scheme, so a nested .portal-dark-theme (e.g. a brochure in its pillar palette) re-resolves them there.
        dark: {
          range: {
            background: '{primary.color}',
          },
          handle: {
            background: '{primary.color}',
          },
        },
      }
    },
    tag: {
      root: {
        fontSize: '{fontSize.sm}',
        fontWeight: '600',
        padding: '0.3rem 0.8rem',
        borderRadius: '{borderRadius.full}'
      },
      colorScheme: {
        light: {
          primary: {
            color: '{primary.600}',
            background: '{primary.200}'
          },
          secondary: {
            color: '{surface.600}',
            background: '{surface.200}'
          },
          success: {
            color: '{success.600}',
            background: '{success.200}'
          },
          info: {
            color: '{info.600}',
            background: '{info.200}'
          },
          warn: {
            color: '{warn.600}',
            background: '{warn.200}'
          },
          danger: {
            color: '{error.600}',
            background: '{error.200}'
          },
        },
        dark: {
          primary: {
            color: '{primary.300}',
            background: 'color-mix(in srgb, {primary.500}, transparent 82%)'
          },
          secondary: {
            color: '{surface.200}',
            background: '{surface.800}'
          },
          success: {
            color: '{success.300}',
            background: 'color-mix(in srgb, {success.500}, transparent 82%)'
          },
          info: {
            color: '{sky.300}',
            background: 'color-mix(in srgb, {sky.500}, transparent 82%)'
          },
          warn: {
            color: '{warn.300}',
            background: 'color-mix(in srgb, {warn.500}, transparent 82%)'
          },
          danger: {
            color: '{error.300}',
            background: 'color-mix(in srgb, {error.500}, transparent 78%)'
          },
        }
      }
    },
    textarea: {
      // Per-scheme for the same reason as inputtext: a nested .portal-dark-theme textarea would otherwise stay light.
      colorScheme: {
        dark: {
          root: {
            background: '{surface.950}',
            filledBackground: '{surface.950}',
            filledHoverBackground: '{surface.950}',
            filledFocusBackground: '{surface.950}',
            borderColor: '{surface.700}',
            hoverBorderColor: '{surface.600}',
            color: '{surface.0}',
            placeholderColor: '{surface.400}',
          },
        },
      },
    },
    floatlabel: {
      root: {
        active: {
          fontSize: '{fontSize.xs}',
        },
      },
    },
    tieredmenu: {
      root: {
        borderRadius: '{borderRadius.md}'
      },
      item: {
        padding: '1rem',
        gap: '.8rem',
      },
      colorScheme: {
        // v21 TieredMenu's `Submenu` token shape diverged from v19's
        // MenuDark.submenu. Casting via `as never` keeps the legacy override
        // applied at runtime; PrimeNG ignores unknown keys silently.
        dark: MenuDark as never,
      }
    },
    toast: {
      icon: {
        size: '{fontSize.lg}',
      },
      summary: {
        fontSize: '{fontSize.base}',
      },
      detail: {
        fontSize: '{fontSize.base}',
        fontWeight: '400',
      },
      // v21 dropped toast.textGap, toast.closeIcon (string → object), and
      // toast.close (the close.icon/close.button overrides were v19-only).
      // PrimeNG defaults handle the close affordance in v21.
    },
    togglebutton: {
      // v21 nests colorScheme.light.root.checkedColor (it was top-level in v19);
      // the icon variant lives at colorScheme.light.icon.checkedColor.
      root: {
        padding: '0.4rem',
        lg: {
          padding: '0.6rem',
          fontSize: '{fontSize.base}', // only fOS's All / My fOS toggle is large today; sized to a large input (4.2rem)
        },
        sm: {
          padding: '0.2rem',
        }
      },
      content: {
        padding: '0.3rem 1rem',
        sm: {
          padding: '0.25rem 0.5rem',
        },
        lg: {
          padding: '0.55rem 1.5rem',
        }
      },
      colorScheme: {
        light: {
          root: {
            checkedColor: '{primary.500}',
          },
          icon: {
            checkedColor: '{primary.500}',
          },
        },
        dark: {
          root: {
            color: '{surface.200}',
            hoverColor: '{surface.0}',
            checkedColor: '{surface.0}',
          },
          icon: {
            color: '{surface.300}',
            hoverColor: '{surface.0}',
            checkedColor: '{surface.0}',
          },
          content: {
            checkedBackground: '{primary.700}',
          },
        },
      },
    },
    toggleswitch: {
      // v21 nests width/height/gap/borderRadius under root.
      root: {
        width: '3.6rem',
        height: '2rem',
        gap: '.4rem',
        borderRadius: '{borderRadius.full}',
      },
      handle: {
        size: '1.2rem',
      },
      colorScheme: {
        light: {
          root: {
            background: '{surface.500}'
          },
          handle: {
            checkedBackground: '{primary.50}',
            checkedHoverBackground: '{primary.100}'
          }
        }
      }
    },
    tooltip: {
      root: {
        maxWidth: '26rem',
      },
    },
    popover: {
      root: {
        arrowOffset: '2.5rem',
      }
    }
  }
});

